/**
 * Pagina "Notifiche" - lista completa delle notifiche dell'utente corrente.
 *
 * Filtri stato/tipo e paginazione (limit/offset) sono nello stato URL via
 * useUrlState/useUrlNumberState (REGOLA HARD del repo: il back-navigation deve
 * ripristinare filtri/pagina della lista, mai in useState locale).
 *
 * Wrapper sugli endpoint /api/v1/notifiche del core. Segue lo stile di lista
 * esistente (es. ClientiList.tsx): componenti solo da @floraltech/ui, italiano
 * hardcoded (le pagine dati della shell non usano i18n, riservato a
 * shell/login/profilo).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUrlNumberState, useUrlState } from '@lib/useUrlState';

/** Notifica cosi' come restituita da GET /api/v1/notifiche (NotificaResponse). */
interface Notifica {
  id: string;
  year: number;
  tipo: string;
  canale: string;
  titolo: string;
  corpo: string | null;
  stato: string;
  priorita: string;
  entita_tipo: string | null;
  entita_id: string | null;
  letta_at: string | null;
  scadenza: string | null;
  meta: { url?: string; [key: string]: unknown } | null;
  created_at: string;
}

const PAGE_SIZE = 20;

/** Header di autenticazione coerente col resto della shell (token da localStorage). */
const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/** Formatta una data ISO in data + ora breve (it-IT). */
function formatData(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Variante Badge per priorita'. */
function badgeVariantPriorita(priorita: string): 'destructive' | 'warning' | 'muted' {
  if (priorita === 'alta') return 'destructive';
  if (priorita === 'bassa') return 'muted';
  return 'warning';
}

/**
 * Pagina lista notifiche con filtri stato/tipo e paginazione, stato in URL.
 */
const NotifichePage: React.FC = () => {
  const navigate = useNavigate();
  const [notifiche, setNotifiche] = useState<Notifica[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [stato, setStato] = useUrlState('stato', '__all__');
  const [tipo, setTipo] = useUrlState('tipo', '');
  const [page, setPage] = useUrlNumberState('page', 0);

  /** Carica la lista notifiche dall'API con i filtri correnti. */
  const caricaNotifiche = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      if (stato !== '__all__') params.set('stato', stato);
      if (tipo.trim()) params.set('tipo', tipo.trim());

      const res = await fetch(`/api/v1/notifiche?${params.toString()}`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      setNotifiche(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [stato, tipo, page]);

  useEffect(() => {
    void caricaNotifiche();
  }, [caricaNotifiche]);

  /** Cambia il filtro stato e torna alla prima pagina. */
  const handleStatoChange = (v: string) => {
    setStato(v);
    setPage(0);
  };

  /** Cambia il filtro tipo e torna alla prima pagina. */
  const handleTipoChange = (v: string) => {
    setTipo(v);
    setPage(0);
  };

  /** Segna una notifica come letta e naviga alla destinazione (o resta qui + refresh). */
  const handleClickNotifica = useCallback(
    async (notifica: Notifica) => {
      try {
        await fetch(`/api/v1/notifiche/${notifica.id}/letta?year=${notifica.year}`, {
          method: 'PATCH',
          headers: authHeaders(),
        });
      } catch {
        // silenzioso
      }
      const url = notifica.meta?.url;
      if (url && url !== '/notifiche') {
        navigate(url);
        return;
      }
      void caricaNotifiche();
    },
    [caricaNotifiche, navigate],
  );

  /** Segna tutte le notifiche come lette e ricarica la lista. */
  const handleSegnaTutteLette = useCallback(async () => {
    try {
      await fetch('/api/v1/notifiche/letta-tutte', {
        method: 'POST',
        headers: authHeaders(),
      });
    } catch {
      // silenzioso
    }
    void caricaNotifiche();
  }, [caricaNotifiche]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Notifiche
        </h1>
        <Button variant="outline" onClick={() => void handleSegnaTutteLette()}>
          Segna tutte lette
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Filtri */}
      <Card>
        <CardHeader>
          <CardTitle>Filtri</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <Select value={stato} onValueChange={handleStatoChange}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Stato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte</SelectItem>
              <SelectItem value="non_letta">Non lette</SelectItem>
              <SelectItem value="letta">Lette</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder="Filtra per tipo (es. alert_fattura_scaduta)"
            value={tipo}
            onChange={(e) => handleTipoChange(e.target.value)}
            className="w-72"
          />
        </CardContent>
      </Card>

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          {loading && (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Caricamento...
            </p>
          )}
          {!loading && !error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stato</TableHead>
                  <TableHead>Titolo</TableHead>
                  <TableHead>Corpo</TableHead>
                  <TableHead>Priorita</TableHead>
                  <TableHead>Data</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notifiche.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="text-center text-sm text-muted-foreground"
                    >
                      Nessuna notifica trovata
                    </TableCell>
                  </TableRow>
                )}
                {notifiche.map((notifica) => (
                  <TableRow
                    key={notifica.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => void handleClickNotifica(notifica)}
                  >
                    <TableCell>
                      {notifica.stato === 'non_letta' && (
                        <Badge variant="accent">Non letta</Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {notifica.titolo}
                    </TableCell>
                    <TableCell className="max-w-md truncate text-sm text-muted-foreground">
                      {notifica.corpo ?? '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={badgeVariantPriorita(notifica.priorita)}>
                        {notifica.priorita}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatData(notifica.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Paginazione */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          disabled={page === 0 || loading}
          onClick={() => setPage(Math.max(0, page - 1))}
        >
          Precedente
        </Button>
        <span className="text-sm text-muted-foreground">Pagina {page + 1}</span>
        <Button
          variant="outline"
          disabled={notifiche.length < PAGE_SIZE || loading}
          onClick={() => setPage(page + 1)}
        >
          Successiva
        </Button>
      </div>
    </div>
  );
};

export default NotifichePage;
